import { deviceFingerprint, wrapDevice } from "@envstash/core-wasm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CheckIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { ErrorText, PageHeader } from "@/components/page";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, errorMessage } from "@/lib/api";
import { platformLabel } from "@/lib/format";
import { useUnlocked } from "@/lib/hooks";

export const Route = createFileRoute("/_app/devices/enroll")({
  validateSearch: z.object({ code: z.string().optional() }),
  component: EnrollPage,
});

function EnrollPage() {
  const { code } = Route.useSearch();
  const s = useUnlocked();
  const nav = useNavigate();
  const qc = useQueryClient();
  const info = useQuery({
    queryKey: ["enroll", code],
    queryFn: () => api.devices.enrollInfo({ user_code: code ?? "" }),
    enabled: Boolean(code),
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<"approved" | "denied" | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const approve = async () => {
    if (!info.data || !code) return;
    setBusy(true);
    setError(null);
    try {
      const device_wrap = wrapDevice(s.vk, info.data.device_id, info.data.public_key);
      await api.devices.enrollApprove({ user_code: code, device_wrap });
      setDone("approved");
      void qc.invalidateQueries({ queryKey: ["devices"] });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const deny = async () => {
    if (!code) return;
    setBusy(true);
    try {
      await api.devices.enrollDeny({ user_code: code });
      setDone("denied");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Approve device"
        description="Only approve a device you just set up yourself. Approval hands it a copy of your vault key."
      />
      <Card className="max-w-lg">
        {!code ? (
          <CardContent className="pt-6">
            <p className="text-sm">
              No code given.{" "}
              <Link to="/devices" className="underline underline-offset-4">
                Enter one on the devices page.
              </Link>
            </p>
          </CardContent>
        ) : done ? (
          <CardContent className="pt-6">
            <Alert data-testid="enroll-result">
              <AlertTitle>{done === "approved" ? "Device approved" : "Request denied"}</AlertTitle>
              <AlertDescription>
                {done === "approved"
                  ? "The CLI will finish signing in within a few seconds."
                  : "The device was not given access."}
              </AlertDescription>
            </Alert>
            <Button className="mt-4" onClick={() => void nav({ to: "/devices" })}>
              Back to devices
            </Button>
          </CardContent>
        ) : info.isLoading ? (
          <CardContent className="pt-6 text-sm text-muted-foreground">Looking up code {code}…</CardContent>
        ) : info.error || !info.data ? (
          <CardContent className="pt-6">
            <Alert>
              <AlertTitle>No pending request for {code}</AlertTitle>
              <AlertDescription>
                Codes expire after 10 minutes. Run `envstash login` again to get a new one.
              </AlertDescription>
            </Alert>
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle>{info.data.name}</CardTitle>
              <CardDescription>
                {platformLabel(info.data.platform)} · code <span className="font-mono">{code}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTitle>Approving gives this Mac full access to your vault</AlertTitle>
                <AlertDescription>
                  Only approve a request you started yourself. The terminal running{" "}
                  <span className="font-mono">envstash login</span> shows the same fingerprint:
                  <code className="ml-1 font-mono text-sm" data-testid="fingerprint">
                    {safeFingerprint(info.data.public_key)}
                  </code>
                </AlertDescription>
              </Alert>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="confirm-fingerprint"
                />
                <span>I started this enrollment and the fingerprint matches my terminal.</span>
              </label>
              <ErrorText error={error} />
              <div className="flex gap-2">
                <Button onClick={() => void approve()} disabled={busy || !confirmed} data-testid="approve">
                  <CheckIcon /> Approve
                </Button>
                <Button variant="outline" onClick={() => void deny()} disabled={busy}>
                  <XIcon /> Deny
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </>
  );
}

function safeFingerprint(publicKey: string): string {
  try {
    return deviceFingerprint(publicKey);
  } catch {
    return "invalid key";
  }
}
